import {useCallback, useEffect} from 'react';
import {useDashboardState} from './useDashboardState';
import {useDashboardUIStore} from '../store/dashboardUIStore';
import {checkAuthStatus} from '../../../utils/authUtils';
import {DashboardViewMode} from '../types/viewMode.types';

/**
 * Unified hook for dashboard management that combines data and UI state
 * This replaces the need for multiple useState calls in components
 */
export const useDashboardManagement = () => {
  // Get UI state first to determine view mode
  const uiStore = useDashboardUIStore();
  const {viewMode} = uiStore;

  // Data from existing hook - pass viewMode to optimize API calls
  const dashboardState = useDashboardState(viewMode);

  // Destructure from dashboardState
  const {
    user, isProvider, brandNames,
    selectedStore, selectedMarketer,
    selectedStoreCount, selectedMarketerCount,
    currentStoresError, marketersError
  } = dashboardState;

  // Destructure from uiStore
  const {
    setViewMode,
    showStores, showMarketers,
    setShowStores, setShowMarketers,
    toggleStores, toggleMarketers,
    isCollapsed, isMobile, isMobileMenuOpen,
    setIsCollapsed, setIsMobile, setIsMobileMenuOpen,
    toggleSidebar, closeMobileMenu,
    reset: resetUI
  } = uiStore;

  // Unified Actions
  const handleViewModeChange = useCallback((mode: DashboardViewMode) => {
    setViewMode(mode);
  }, [setViewMode]);

  const handleToggleStores = useCallback(() => {
    toggleStores();
  }, [toggleStores]);

  const handleToggleMarketers = useCallback(() => {
    toggleMarketers();
  }, [toggleMarketers]);

  const handleToggleSidebar = useCallback(() => {
    toggleSidebar();
  }, [toggleSidebar]);

  const handleCloseMobileMenu = useCallback(() => {
    closeMobileMenu();
  }, [closeMobileMenu]);

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024; // Increased breakpoint for better tablet support
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(true);
        setIsMobileMenuOpen(false);
      } else {
        // On desktop, ensure sidebar is expanded by default
        setIsCollapsed(false);
        setIsMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [setIsMobile, setIsCollapsed, setIsMobileMenuOpen]);

  // Check auth status when Dashboard mounts
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Reset all stores
  const resetAllStores = useCallback(() => {
    resetUI();
  }, [resetUI]);

  return {
    // UI State
    viewMode, setViewMode,
    showStores, showMarketers,
    isCollapsed, isMobile, isMobileMenuOpen,

    // Data
    user, isProvider, brandNames,
    selectedStore, selectedMarketer,
    selectedStoreCount, selectedMarketerCount,
    currentStoresError, marketersError,

    // Actions
    handleViewModeChange, handleToggleSidebar, handleCloseMobileMenu,
    handleToggleStores, handleToggleMarketers,
    resetAllStores
  };
};
